import {
  ROOT,
  URL_SEARCH,
  FACETS_ACTIVITIES_NAME,
  FACETS_LOCATION_NAME,
  QUERY_PARAM_LOCATION,
  QUERY_PARAM_GEO_LOCATION_ID,
  QUERY_PARAM_DISTANCE,
  QUERY_PARAM_PREV_LOCATION,
  QUERY_PARAM_ANY_LOCATION,
} from '../../constants';
import { SETUP_DATA, RECORD_CLICK, HANDLE_CLICK } from './_types';
import {
  SET_BREADCRUMBS,
  SET_SEARCH,
  SET_SEARCH_RESULTS,
  SET_FACETS,
  SET_APPLIED_REFINEMENTS,
  SET_TITLE,
  SET_PAGE_META_DATA,
} from '../mutations/_types';
import { CLICK, HANDLE_LINK_CLICK } from '../../../root/actions/_types';

export default ({ pageName }) => ({
  /**
   * This method is required as the searh implementation won't perform a full refresh
   * of the page after calling the search API.
   * Setup the facets, refinements and search results
   * @param {Object} [data] Search call response or undefined when first loaded
   */
  [SETUP_DATA]: (
    { rootState, commit },
    {
      title,
      canonical,
      requestUriPath, // API should return canonical also
      breadcrumbs,
      search,
    },
  ) => {
    const { results, totalResults, totalPages, currentPage, pageSize, facets } = search || {};

    if (!facets || !facets.length) return null;

    const { search: searchParams, pathname } = rootState.root;

    const QUERY_PARAMS = /[?&]([a-zA-Z.]+)=([^&]+)/gm;
    const FACET_PATTERNS = {
      [FACETS_LOCATION_NAME]: {
        pattern: /\/p\/[^/]+/gm,
        removeParams: [
          QUERY_PARAM_LOCATION,
          QUERY_PARAM_GEO_LOCATION_ID,
          QUERY_PARAM_DISTANCE,
          QUERY_PARAM_PREV_LOCATION,
          QUERY_PARAM_ANY_LOCATION,
        ],
        ignoreRefinement: true,
      },
      [FACETS_ACTIVITIES_NAME]: {
        pattern: /\/a\/[^/]+/gm,
      },
    };

    const setupFacetItems = (items, facetName) =>
      items.map(function p({
        browserLink: bLink,
        canonicalLink: cLink,
        queryString,
        selected,
        children,
        ...rest
      }) {
        // Setup canonical and browser links.
        // NOTE: Facets that does not have any pattern won't have canonical link
        const facetPattern = FACET_PATTERNS[facetName];
        let canonicalLink = '';
        let browserLink = '';
        let ignoreRefinement;

        let nextQuery = new URLSearchParams(searchParams);

        // Ignore the following params
        nextQuery.delete('promptDatepicker');
        nextQuery.delete('page');

        // Refinements does not have a pattern, so add the query params
        if (!facetPattern) {
          // matchAll is supported in node > 12, so get setup results here
          const queryParams = [];
          let hasMatch = true;
          while (hasMatch) {
            const tmpResult = QUERY_PARAMS.exec(`?${queryString}`);
            if (tmpResult !== null) queryParams.push(tmpResult);
            else hasMatch = false;
          }

          let values = [];

          if (selected) {
            (queryParams.length > 0 ? queryParams : []).forEach(([, key]) => {
              values = nextQuery.getAll(key);
              nextQuery.delete(key);

              values.forEach((param) => {
                if (queryString.indexOf(param) === -1) nextQuery.append(key, param);
              });
            });
          } else {
            nextQuery = queryParams.reduce((acc, cur) => {
              const [, key, value] = cur;
              acc.append(key, value);

              return acc;
            }, nextQuery);
          }

          const allParams = Array.from(nextQuery.values());
          browserLink = allParams && allParams.length ? `?${nextQuery.toString()}` : pathname;
        } else {
          const {
            pattern,
            removeParams = [],
            ignoreRefinement: ignoreRefinementItem,
          } = facetPattern;

          // Ignore params if required
          removeParams.forEach((paramKey) => nextQuery.delete(paramKey));

          const allParams = Array.from(nextQuery.values());
          const prefixParams = allParams && allParams.length ? '?' : '';

          canonicalLink = cLink ? `${ROOT}${cLink}` : '';

          let prefixLink = '';
          if (selected) {
            const newPathname = pathname.replace(pattern, '');
            prefixLink = newPathname === ROOT ? URL_SEARCH : newPathname;
          } else {
            prefixLink = `${
              pathname === URL_SEARCH ? ROOT : pathname.replace(pattern, '')
            }${cLink}`;
          }

          browserLink = `${prefixLink}${prefixParams}${nextQuery.toString()}`;
          ignoreRefinement = ignoreRefinementItem;
        }

        return {
          browserLink,
          canonicalLink,
          queryString,
          selected,
          ignoreRefinement,
          children: (children || []).map(p),
          ...rest,
        };
      });

    const breadcrumbsArr = Object.keys(breadcrumbs || {}).reduce((acc, cur) => {
      const copyArr = acc;
      const { name, data: canonicalLink } = breadcrumbs[cur];
      copyArr[Number(cur)] = {
        label: name,
        link: canonicalLink,
        canonicalLink,
        browserLink: canonicalLink,
        forward: canonicalLink === ROOT,
      };
      return copyArr;
    }, []);
    commit(SET_BREADCRUMBS, breadcrumbsArr);

    const searchObj = {
      totalResults,
      totalPages,
      currentPage,
      pageSize,
    };
    commit(SET_SEARCH, searchObj);

    commit(SET_SEARCH_RESULTS, results);

    const facetsArr = facets.map(({ name, items, ...rest }) => ({
      name,
      items: setupFacetItems(items, name),
      ...rest,
    }));
    commit(SET_FACETS, facetsArr);

    // Refresh applied refinements
    const appliedRefinementsArr = [];
    const appliedRefinements = facetsArr
      .reduce(function f(acc, cur) {
        return [
          ...acc,
          ...(cur.items && cur.items.length
            ? cur.items.map((i) => ({ ...i, value: cur.name })).reduce(f, [])
            : (() => {
                const { children, ...rest } = cur;
                return cur.children && cur.children.length
                  ? cur.children.reduce(f, [{ ...rest, value: rest.name }])
                  : [{ ...cur, value: cur.name }];
              })()),
        ];
      }, [])
      .filter((r) => r.selected && !r.ignoreRefinement);

    const currentQuery = new URLSearchParams(searchParams);
    currentQuery.delete('promptDatepicker');
    currentQuery.delete('page');

    // Setup refinements in the same query string params order and add the facets with hub pages also
    appliedRefinements.forEach((r) => {
      if (r.canonicalLink) {
        appliedRefinementsArr.push(r);
      }
    });

    currentQuery.forEach((value) => {
      const refinement = appliedRefinements.find(
        ({ queryString }) => queryString && queryString.indexOf(value) > -1,
      );
      if (
        !!refinement &&
        appliedRefinementsArr.findIndex((r) => r.name === refinement.name) === -1
      ) {
        appliedRefinementsArr.push(refinement);
      }
    });
    commit(SET_APPLIED_REFINEMENTS, appliedRefinementsArr);

    // Update metadata info
    commit(SET_TITLE, title);
    commit(SET_PAGE_META_DATA, {
      canonical: canonical || requestUriPath,
    });
  },
  [RECORD_CLICK]: ({ dispatch }, payload) => {
    // convenience method for recording analytics
    const { section, linkText, ...rest } = payload;
    const sectionText = section ? `${section}_` : '';
    const clickData = {
      linkName: `${pageName}_${sectionText}${linkText}`,
      // linkName is overridable by putting "linkName" in the payload
      ...rest,
    };
    return dispatch(CLICK, clickData, { root: true });
  },
  [HANDLE_CLICK]: ({ dispatch }, payload) => {
    // convenience method to handle recording analytics on link clicks
    // and navigation (via window.history API)
    const { analytics, ...nextPayload } = payload;
    if (payload.event.button !== 0) return;
    const analyticsPromise = dispatch(RECORD_CLICK, analytics);
    if (payload.external) {
      nextPayload.before = Promise.resolve(analyticsPromise).then(Promise.resolve(payload.before));
    }
    dispatch(HANDLE_LINK_CLICK, nextPayload, { root: true });
  },
});
